/**
 * What the repository index (YAG-831) puts on `/services/repositories`: the
 * description row under the name with Edit and Re-index, the lines saying
 * what a build cannot do here, and the checks the repository needs; and
 * (YAG-1067) the Role and Related repositories rows, with their words.
 *
 * The index wording is worked out in `environmentPage.ts`; the role and
 * relation words are here, beside their rows.
 */
import { useState } from "react";
import {
  REPO_INDEX_DESCRIPTION_MAX,
  REPO_ROLES,
  type EffectiveRepoRelation,
  type RepoIndex,
  type RepoIndexCheckRequirement,
  type RepoRelationKind,
  type RepoRelationsView,
  type RepoRelationWrite,
  type RepoRole,
} from "@yagni/shared";
import { Button, Textarea } from "../ui/index.js";
import { Row } from "./environmentControls.js";
import { checkLists, indexLine, indexWarnings } from "./environmentPage.js";

/** The sub-line a repository with an index and no environment carries. */
export const INFERRED_INSTALL_LINE = "Builds use the inferred install";

export function RepositoryDescription({
  index,
  notSetUp,
  busy,
  error,
  onReindex,
  onSave,
}: {
  index: RepoIndex | null;
  /** The environment has never been set up, so builds read the index. */
  notSetUp: boolean;
  busy: boolean;
  error: string;
  onReindex: () => void;
  /** Resolves true when the server took it; null clears to the inferred text. */
  onSave: (description: string | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const line = indexLine(index);
  const warnings = indexWarnings(index);

  return (
    <div className="qa-index" data-testid="repository-description">
      {line.kind === "indexing" && (
        <p className="qa-index-line qa-hint" data-testid="repository-indexing">
          Indexing
        </p>
      )}
      {line.kind === "failed" && (
        <p className="qa-index-line" data-testid="repository-index-failed">
          <span>{line.message}</span>{" "}
          <button type="button" className="wrk-quiet-btn" disabled={busy} onClick={onReindex}>
            Retry
          </button>
        </p>
      )}
      {line.kind === "described" && !editing && (
        <p className="qa-index-line">
          <span data-testid="repository-description-text">
            {line.text ?? "No description yet."}
          </span>{" "}
          <button
            type="button"
            className="wrk-quiet-btn"
            disabled={busy}
            onClick={() => {
              setDraft(line.text ?? "");
              setEditing(true);
            }}
          >
            Edit
          </button>{" "}
          <button type="button" className="wrk-quiet-btn" disabled={busy} onClick={onReindex}>
            Re-index
          </button>
        </p>
      )}
      {line.kind === "described" && editing && (
        <div className="qa-index-edit">
          <Textarea
            aria-label="Repository description"
            value={draft}
            maxLength={REPO_INDEX_DESCRIPTION_MAX}
            rows={3}
            disabled={busy}
            onChange={(event) => setDraft(event.target.value)}
          />
          <span className="qa-hint">
            {draft.trim()
              ? `${draft.length}/${REPO_INDEX_DESCRIPTION_MAX}`
              : "Empty goes back to the inferred description."}
          </span>
          <span className="qa-index-actions">
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void onSave(draft.trim() ? draft.trim() : null).then((ok) => {
                  if (ok) setEditing(false);
                })
              }
            >
              Save description
            </Button>
            <button
              type="button"
              className="wrk-quiet-btn"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </span>
        </div>
      )}
      {notSetUp && index?.status === "indexed" && (
        <p className="qa-index-line qa-hint" data-testid="repository-inferred-install">
          {INFERRED_INSTALL_LINE}
        </p>
      )}
      {warnings.length > 0 && (
        <ul className="qa-index-warnings" aria-label="What builds cannot do here">
          {warnings.map((warning) => (
            <li key={warning.key} title={warning.detail}>
              {warning.line}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="qa-notice">
          {error}
        </p>
      )}
    </div>
  );
}

function CheckList({ label, checks }: { label: string; checks: RepoIndexCheckRequirement[] }) {
  return (
    <Row title={label}>
      <span className="wrk-srow-controls">
        {checks.length ? (
          <ul className="qa-index-checks" aria-label={label}>
            {checks.map((check) => (
              <li key={`${check.kind}:${check.name}:${check.seenIn}`}>
                {check.name}
                <span className="qa-fact"> · {check.seenIn}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="qa-hint">None</span>
        )}
      </span>
    </Row>
  );
}

/** What each role is called on the sheet, and what agents do with it (YAG-1067). */
export const ROLE_WORDS: Record<RepoRole, { label: string; does: string }> = {
  application: { label: "Application", does: "read when a ticket depends on it" },
  library: { label: "Library", does: "code other repositories consume" },
  environment: {
    label: "Environment",
    does: "defines the services other repositories run beside",
  },
  documentation: { label: "Documentation", does: "read before planning" },
  archive: { label: "Archived", does: "listed last, never read first" },
};

/** The Role row's summary: "Documentation · read before planning". */
export function roleSummary(index: RepoIndex | null): string {
  if (!index?.role) return index?.status === "indexed" ? "Not found yet" : "Not indexed yet";
  const words = ROLE_WORDS[index.role];
  return `${words.label} · ${words.does}`;
}

/** One edge, as this repository sees it, in plain words. */
export function relationLine(edge: EffectiveRepoRelation): string {
  const other = edge.otherFullName;
  const from = edge.otherIs === "from";
  if (edge.kind === "documents")
    return from ? `${other} documents this repository` : `Documents ${other}`;
  if (edge.kind === "defines_environment_for")
    return from
      ? `${other} defines this repository's services`
      : `Defines the services of ${other}`;
  return from ? `${other} uses this repository` : `Uses ${other}`;
}

/** Why the edge is there: its evidence and who said it. */
export function relationEvidence(edge: EffectiveRepoRelation): string {
  const facts: string[] = [];
  const { evidence } = edge;
  if (evidence.composeFile)
    facts.push(
      evidence.service
        ? `${evidence.composeFile}, service ${evidence.service}`
        : evidence.composeFile,
    );
  if (evidence.pages?.length) facts.push(evidence.pages.slice(0, 3).join(", "));
  const said =
    edge.source === "person"
      ? edge.denied
        ? "Removed by a person; the index will not add it again"
        : "added by a person"
      : edge.source === "customer_instructions"
        ? `said in ${evidence.file ?? "an instruction file"}`
        : evidence.composeFile
          ? "found in its compose file"
          : "found in its pages";
  return [...facts, said].join(" · ");
}

/** The Related repositories row's summary: the first edge, and how many more. */
export function relationsSummary(view: RepoRelationsView | null): string {
  const live = (view?.relations ?? []).filter((edge) => !edge.denied);
  if (!live.length) return "None found";
  return live.length === 1
    ? relationLine(live[0]!)
    : `${relationLine(live[0]!)} and ${live.length - 1} more`;
}

/** The phrasings Add offers, each completing "<repository> …". */
export const RELATION_CHOICES: {
  value: string;
  label: string;
  kind: RepoRelationKind;
  direction: "from" | "to";
}[] = [
  { value: "documents", label: "documents this repository", kind: "documents", direction: "from" },
  {
    value: "documented",
    label: "is documented by this repository",
    kind: "documents",
    direction: "to",
  },
  {
    value: "defines",
    label: "defines the services of this repository",
    kind: "defines_environment_for",
    direction: "from",
  },
  {
    value: "services",
    label: "gets its services from this repository",
    kind: "defines_environment_for",
    direction: "to",
  },
  { value: "uses", label: "uses this repository", kind: "depends_on", direction: "from" },
];

export function RepositoryRole({
  index,
  busy,
  onSave,
}: {
  index: RepoIndex | null;
  busy: boolean;
  /** Null goes back to what the index finds. */
  onSave: (role: RepoRole | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RepoRole>(index?.role ?? "application");
  const role = index?.role;
  return (
    <div className="qa-index" data-testid="repository-role">
      {!editing && (
        <p className="qa-index-line">
          <span data-testid="repository-role-text">{roleSummary(index)}</span>
          {role && (
            <span className="qa-hint">
              {" "}
              ·{" "}
              {index?.roleSource === "person" ? "set by a person" : (index?.roleReason ?? "found")}
            </span>
          )}{" "}
          <button
            type="button"
            className="wrk-quiet-btn"
            disabled={busy}
            onClick={() => {
              setDraft(role ?? "application");
              setEditing(true);
            }}
          >
            Edit
          </button>
        </p>
      )}
      {editing && (
        <div className="qa-index-edit">
          <select
            className="wrk-control"
            aria-label="Repository role"
            value={draft}
            disabled={busy}
            onChange={(event) => setDraft(event.target.value as RepoRole)}
          >
            {REPO_ROLES.map((value) => (
              <option key={value} value={value}>
                {ROLE_WORDS[value].label}: {ROLE_WORDS[value].does}
              </option>
            ))}
          </select>
          <span className="qa-index-actions">
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void onSave(draft).then((ok) => {
                  if (ok) setEditing(false);
                })
              }
            >
              Save role
            </Button>
            {index?.roleSource === "person" && (
              <button
                type="button"
                className="wrk-quiet-btn"
                disabled={busy}
                onClick={() =>
                  void onSave(null).then((ok) => {
                    if (ok) setEditing(false);
                  })
                }
              >
                Use what the index finds
              </button>
            )}
            <button
              type="button"
              className="wrk-quiet-btn"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

export function RepositoryRelations({
  view,
  repositories,
  repoId,
  busy,
  error,
  onWrite,
}: {
  view: RepoRelationsView;
  /** The workspace's repositories, for Add. */
  repositories: { id: string; owner: string; name: string }[];
  repoId: string;
  busy: boolean;
  error: string;
  onWrite: (write: RepoRelationWrite) => Promise<boolean>;
}) {
  const [other, setOther] = useState("");
  const [choice, setChoice] = useState(RELATION_CHOICES[0]!.value);
  const others = repositories.filter((repo) => repo.id !== repoId);
  const picked = RELATION_CHOICES.find((option) => option.value === choice)!;
  return (
    <div className="qa-index" data-testid="repository-relations">
      {view.relations.length ? (
        <ul className="qa-index-checks" aria-label="Related repositories">
          {view.relations.map((edge) => (
            <li key={`${edge.otherRepoId}:${edge.kind}:${edge.otherIs}`}>
              <span className={edge.denied ? "qa-hint" : undefined}>{relationLine(edge)}</span>
              <span className="qa-fact"> · {relationEvidence(edge)}</span>{" "}
              <button
                type="button"
                className="wrk-quiet-btn"
                disabled={busy}
                onClick={() =>
                  void onWrite({
                    otherRepoId: edge.otherRepoId,
                    kind: edge.kind,
                    direction: edge.otherIs,
                    stance: edge.denied ? null : "denies",
                  })
                }
              >
                {edge.denied ? "Undo" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="qa-index-line qa-hint">None found</p>
      )}
      {view.unlinkedPointers.map((pointer) => (
        <p
          key={`${pointer.file}:${pointer.repository}`}
          className="qa-index-line qa-hint"
          data-testid="repository-unlinked-pointer"
        >
          {pointer.file} names {pointer.repository}, which this workspace has not linked
        </p>
      ))}
      {others.length > 0 && (
        <div className="qa-index-edit">
          <select
            className="wrk-control"
            aria-label="Related repository"
            value={other}
            disabled={busy}
            onChange={(event) => setOther(event.target.value)}
          >
            <option value="" disabled>
              Choose a repository
            </option>
            {others.map((repo) => (
              <option key={repo.id} value={repo.id}>
                {repo.owner}/{repo.name}
              </option>
            ))}
          </select>
          <select
            className="wrk-control"
            aria-label="How it relates"
            value={choice}
            disabled={busy}
            onChange={(event) => setChoice(event.target.value)}
          >
            {RELATION_CHOICES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            disabled={busy || !other}
            onClick={() =>
              void onWrite({
                otherRepoId: other,
                kind: picked.kind,
                direction: picked.direction,
                stance: "asserts",
              }).then((ok) => {
                if (ok) setOther("");
              })
            }
          >
            Add
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="qa-notice">
          {error}
        </p>
      )}
    </div>
  );
}

/** "Checks this repo needs": what a build can run, and what needs CI or a person. */
export function CheckRequirements({ index }: { index: RepoIndex | null }) {
  const { runnable, elsewhere } = checkLists(index);
  if (!runnable.length && !elsewhere.length) return null;
  return (
    <div className="qa-index-requirements" data-testid="qa-check-requirements">
      <Row
        title="Checks this repo needs"
        help="Read from compose files, workflows, Dockerfiles and Procfiles."
      />
      <CheckList label="Runnable in a build" checks={runnable} />
      <CheckList label="Needs CI or a person" checks={elsewhere} />
    </div>
  );
}
